import type {
  ReconcileSchedulingPatientRequest,
  ReconcileSchedulingPatientResponse,
  SchedulingBookingMatchSource,
  SchedulingChartMatch,
  SchedulingChartMatchSignal,
} from "@emr/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from "react";
import type { PatientDirectoryEntry } from "../../api/clinicalWorkspace";
import { intakeErrorMessage, isChartMatchesChanged } from "../../api/schedulingIntake";
import {
  AUDITED_READ,
  type IntakeReviewClient,
  schedulingPatientMatchesKey,
} from "./intakeReviewQueries";
import { formatInClinic } from "./scheduleTime";

const SIGNAL_LABELS: Readonly<Record<SchedulingChartMatchSignal, string>> = {
  name_and_dob: "name and date of birth",
  health_number: "health card number",
  dob_and_phone: "date of birth and phone",
};

/** What a returning patient gave when booking online (PA-10), never the number itself. */
const BOOKED_WITH_NOTES: Readonly<Record<SchedulingBookingMatchSource, string>> = {
  verified_health_card: "The patient confirmed their health card and date of birth online.",
  health_card:
    "The patient gave a health card number when booking. The number is not kept: only the charts it matched.",
  details:
    "The patient booked with their name, date of birth and phone number, without a health card number.",
};

const SEX_LABELS: Readonly<Record<string, string>> = {
  female: "Female",
  male: "Male",
  other: "Other",
  unknown: "Sex not recorded",
};

function matchFacts(match: SchedulingChartMatch, timeZone: string): string {
  return [
    `DOB ${match.dateOfBirth}`,
    match.administrativeSex === undefined ? undefined : SEX_LABELS[match.administrativeSex],
    `MRN ${match.mrn}`,
    match.lastVisitAt === undefined
      ? undefined
      : `last visit ${formatInClinic(match.lastVisitAt, timeZone, { dateStyle: "medium" })}`,
  ]
    .filter((part): part is string => part !== undefined)
    .join(" · ");
}

/**
 * A chart the patient's health card matched although its date of birth differs: the strongest
 * sign of a date of birth typed wrong (PA-10, lead decision I4). It can be linked only by giving
 * the booking the chart's date of birth, after staff checked the patient's card.
 */
function correctable(match: SchedulingChartMatch): boolean {
  return !match.linkable && match.matchedOn.includes("health_number");
}

/** A probable chart: who it is, why it matched, and "Link this chart" when it can be linked. */
function MatchRow({
  match,
  timeZone,
  disabled,
  linking,
  onLink,
  onCorrect,
}: {
  readonly match: SchedulingChartMatch;
  readonly timeZone: string;
  readonly disabled: boolean;
  readonly linking: boolean;
  readonly onLink: (patientId: string) => void;
  /** Asks before linking a card match with another date of birth; absent when not offered. */
  readonly onCorrect?: ((match: SchedulingChartMatch) => void) | undefined;
}) {
  return (
    <li>
      <div>
        <strong>{match.displayName}</strong>
        <span>{matchFacts(match, timeZone)}</span>
        <small>
          Matched on {match.matchedOn.map((signal) => SIGNAL_LABELS[signal]).join(", ")}
        </small>
      </div>
      {match.linkable ? (
        <button
          type="button"
          className="schedule-btn ghost sm"
          disabled={disabled}
          aria-label={`Link the chart of ${match.displayName}, MRN ${match.mrn}`}
          onClick={() => onLink(match.patientId)}
        >
          {linking ? "Linking…" : "Link this chart"}
        </button>
      ) : onCorrect !== undefined && correctable(match) ? (
        <div className="intake-review-correct">
          <small className="intake-review-caution">
            Different date of birth from the booking. Check the patient's health card and birth date
            first.
          </small>
          <button
            type="button"
            className="schedule-btn ghost sm"
            disabled={disabled}
            data-correct-chart={match.patientId}
            aria-label={`Link the chart of ${match.displayName}, MRN ${match.mrn}, and correct the booking's date of birth`}
            onClick={() => onCorrect(match)}
          >
            Use the chart's birth date…
          </button>
        </div>
      ) : (
        <small className="intake-review-caution">
          Different date of birth from the booking: can't be linked
        </small>
      )}
    </li>
  );
}

/**
 * "Correct the booking's date of birth?": asked in place, inside the dialog (a second dialog over
 * a Schedule dialog would fight its focus trap), with the safe choice focused. Cancel and Escape
 * (through the dialog's `escapeHandler`) back out of the question, not the dialog, and focus
 * returns to the button that asked it. It says what changes and what stays; the server audits
 * the change without either date.
 */
function CorrectDateOfBirth({
  match,
  bookingDateOfBirth,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly match: SchedulingChartMatch;
  readonly bookingDateOfBirth: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancel.current?.focus();
  }, []);
  return (
    <fieldset className="intake-review-confirm">
      <legend>Correct the booking's date of birth?</legend>
      <p>
        The booking says <strong>{bookingDateOfBirth}</strong>. The chart of{" "}
        <strong>
          {match.displayName} · MRN {match.mrn}
        </strong>{" "}
        says <strong>{match.dateOfBirth}</strong> and has the health card the patient gave.
      </p>
      <ul>
        <li>The booking takes the chart's date of birth and is linked to this chart.</li>
        <li>
          The chart does not change. The patient's links then ask for the chart's date of birth.
        </li>
      </ul>
      <p>Do this only after checking the patient's health card and date of birth.</p>
      <div className="intake-review-actions">
        <button
          ref={cancel}
          type="button"
          className="schedule-btn ghost"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="button" className="schedule-btn primary" disabled={busy} onClick={onConfirm}>
          {busy ? "Linking…" : "Correct and link"}
        </button>
      </div>
    </fieldset>
  );
}

type ChartDirectorySearch = NonNullable<IntakeReviewClient["listPatientDirectory"]>;

/**
 * What Escape does first inside the reconciler, set while it asks something in place: the dialog
 * calls it from ScheduleModal's onEscape, and it returns true when it used the key.
 */
export type ReconcilerEscape = RefObject<(() => boolean) | null>;

/**
 * Any chart in this clinic, by name or MRN, for a patient the matcher missed (a changed surname,
 * a new phone, no card). Linking still goes through the one reconcile path, which accepts only an
 * active chart with the booking's date of birth.
 */
function ChartSearch({
  listPatientDirectory,
  siteId,
  disabled,
  linkingId,
  onLink,
}: {
  readonly listPatientDirectory: ChartDirectorySearch;
  readonly siteId: string;
  readonly disabled: boolean;
  readonly linkingId: string | null;
  readonly onLink: (patientId: string) => void;
}) {
  const inputId = useId();
  const hintId = useId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<readonly PatientDirectoryEntry[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const term = query.trim();
  const search = async () => {
    if (term.length < 2) return;
    const current = ++sequence.current;
    setSearching(true);
    setError(null);
    try {
      const found = await listPatientDirectory(siteId, term, 10);
      if (current === sequence.current) setResults(found.items);
    } catch (caught) {
      if (current === sequence.current) {
        setResults(null);
        setError(intakeErrorMessage(caught, "The chart search failed. Try again."));
      }
    } finally {
      if (current === sequence.current) setSearching(false);
    }
  };
  return (
    <div className="intake-review-search">
      <form
        aria-label="Find another chart"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <label className="schedule-modal-field" htmlFor={inputId}>
          Not listed? Find the chart
          <input
            id={inputId}
            value={query}
            placeholder="Name or MRN"
            autoComplete="off"
            aria-describedby={hintId}
            onChange={(event) => {
              setQuery(event.target.value);
              setResults(null);
              setError(null);
              sequence.current += 1;
              setSearching(false);
            }}
          />
        </label>
        <button
          type="submit"
          className="schedule-btn ghost"
          disabled={searching || term.length < 2}
        >
          {searching ? "Searching…" : "Search"}
        </button>
      </form>
      <p className="intake-review-note" id={hintId}>
        Only a chart with the booking's date of birth can be linked.
      </p>
      {results?.length === 0 ? (
        <p className="intake-review-note" role="status">
          No chart in this clinic has that name or MRN.
        </p>
      ) : null}
      {results !== null && results.length > 0 ? (
        <ul className="intake-review-matches" aria-label="Charts found">
          {results.map((patient) => (
            <li key={patient.id}>
              <div>
                <strong>{patient.displayName}</strong>
                <span>
                  {[
                    `MRN ${patient.mrn}`,
                    patient.age === undefined ? undefined : `age ${patient.age}`,
                    patient.administrativeSex === undefined
                      ? undefined
                      : SEX_LABELS[patient.administrativeSex],
                  ]
                    .filter((part): part is string => part !== undefined)
                    .join(" · ")}
                </span>
              </div>
              <button
                type="button"
                className="schedule-btn ghost sm"
                disabled={disabled}
                aria-label={`Link the chart of ${patient.displayName}, MRN ${patient.mrn}`}
                onClick={() => onLink(patient.id)}
              >
                {linkingId === patient.id ? "Linking…" : "Link this chart"}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p className="schedule-modal-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The booking's one chart path (lead decision S1): the probable charts in this clinic for the
 * patient's answers, then link one, create a chart from the answers, or record that none is the
 * patient. Creating needs every listed chart ruled out first, and the server refuses it if the
 * list changed meanwhile (`chart_matches_changed`), so a duplicate chart is never made by
 * accident. A chart with a different date of birth from the booking is shown but cannot be
 * linked as it stands. A chart ruled out by mistake can still be linked, and any other chart in
 * the clinic can be found by name or MRN and linked, so the desk never has to create a duplicate
 * of a chart it knows.
 *
 * PA-10 reuses it for a returning patient's online booking before any questionnaire (Review
 * booking, `askForDetails`): the read's basis is the booking, there are no answers to create a
 * chart from, "Not this patient" rules the matches out directly, and the dialog's "Ask for
 * patient details" takes the create button's place. In both uses, a chart the patient's health
 * card matched under another date of birth can be linked by giving the booking the chart's date
 * of birth, after an in-place confirmation, so a date of birth typed wrong never forces a
 * duplicate chart.
 */
export function IntakeChartReconciler({
  appointmentId,
  expectedVersion,
  identityAvailable,
  bookingDateOfBirth,
  timeZone,
  client,
  siteId,
  askForDetails,
  escapeHandler,
  onReconciled,
}: {
  readonly appointmentId: string;
  /** The booking's version; undefined when the schedule has not loaded it (nothing can change). */
  readonly expectedVersion: number | undefined;
  /** The answers include who the patient is, so a chart can be created from them. */
  readonly identityAvailable: boolean;
  /**
   * The booking's date of birth, for the correction question. Without it (the schedule has not
   * loaded the booking) a card match with another date of birth cannot be linked from here.
   */
  readonly bookingDateOfBirth?: string | undefined;
  readonly timeZone: string;
  readonly client: Pick<
    IntakeReviewClient,
    "getSchedulingPatientMatches" | "reconcileSchedulingPatient" | "listPatientDirectory"
  >;
  readonly siteId: string;
  /**
   * Review booking (PA-10): there is no questionnaire to create a chart from. Rendered where
   * "Create a chart from these answers" would be, given the number of charts still to decide on.
   */
  readonly askForDetails?: ((openMatches: number) => ReactNode) | undefined;
  /**
   * Set while the date-of-birth question is open, so Escape cancels it instead of closing the
   * dialog. The dialog passes `() => escapeHandler.current?.() === true` as ScheduleModal's
   * onEscape.
   */
  readonly escapeHandler?: ReconcilerEscape | undefined;
  readonly onReconciled: (
    result: ReconcileSchedulingPatientResponse,
    request: ReconcileSchedulingPatientRequest,
  ) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const listId = useId();
  const ruledOutId = useId();
  const matchesKey = schedulingPatientMatchesKey(siteId, appointmentId);
  const matches = useQuery({
    queryKey: matchesKey,
    queryFn: ({ signal }) => client.getSchedulingPatientMatches(appointmentId, signal),
    ...AUDITED_READ,
  });
  const [noneOfThese, setNoneOfThese] = useState(false);
  const [correcting, setCorrecting] = useState<SchedulingChartMatch | null>(null);
  const [busy, setBusy] = useState<ReconcileSchedulingPatientRequest["action"] | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  /** The chart whose "Use the chart's birth date…" asked the question that was just cancelled. */
  const refocusCorrect = useRef<string | null>(null);
  // Cancel and Escape back out of the question, never mid-request, and focus returns to its button.
  useEffect(() => {
    if (correcting !== null || refocusCorrect.current === null) return;
    const patientId = refocusCorrect.current;
    refocusCorrect.current = null;
    root.current?.querySelector<HTMLElement>(`[data-correct-chart="${patientId}"]`)?.focus();
  }, [correcting]);
  useEffect(() => {
    if (escapeHandler === undefined || correcting === null) return;
    const patientId = correcting.patientId;
    escapeHandler.current = () => {
      if (busy === null) {
        refocusCorrect.current = patientId;
        setCorrecting(null);
        setError(null);
      }
      return true;
    };
    return () => {
      escapeHandler.current = null;
    };
  }, [busy, correcting, escapeHandler]);
  const open = (matches.data?.matches ?? []).filter((match) => !match.dismissed);
  const dismissed = (matches.data?.matches ?? []).filter((match) => match.dismissed);
  const ruledOut = open.length === 0 || noneOfThese;

  const run = async (input: ReconcileSchedulingPatientRequest) => {
    if (busy !== null) return;
    setBusy(input.action);
    setLinkingId(input.action === "link" ? input.patientId : null);
    setError(null);
    try {
      const result = await client.reconcileSchedulingPatient(appointmentId, input);
      setCorrecting(null);
      if (result.outcome === "mismatch") {
        setNoneOfThese(false);
        await queryClient.invalidateQueries({ queryKey: matchesKey });
      }
      await onReconciled(result, input);
    } catch (caught) {
      if (isChartMatchesChanged(caught)) {
        setNoneOfThese(false);
        setCorrecting(null);
        await queryClient.invalidateQueries({ queryKey: matchesKey });
      }
      setError(intakeErrorMessage(caught, "The chart could not be updated for this booking."));
    } finally {
      setBusy(null);
      setLinkingId(null);
    }
  };

  if (matches.isPending) {
    return (
      <p className="intake-review-note" role="status">
        Looking for charts in this clinic…
      </p>
    );
  }
  if (matches.isError) {
    return (
      <div className="schedule-modal-error" role="alert">
        <span>{intakeErrorMessage(matches.error, "The possible charts could not be loaded.")}</span>
        <button
          type="button"
          className="schedule-btn ghost sm"
          onClick={() => void matches.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }
  const versionMissing = expectedVersion === undefined;
  const ids = open.map((match) => match.patientId);
  const link = (patientId: string) => {
    if (expectedVersion !== undefined) void run({ action: "link", expectedVersion, patientId });
  };
  const linkDisabled = busy !== null || versionMissing;
  const onCorrect = bookingDateOfBirth === undefined || versionMissing ? undefined : setCorrecting;
  const listPatientDirectory = client.listPatientDirectory;
  // Review booking: the booking's own matches, with no answers to create a chart from.
  const bookingReview = askForDetails !== undefined;
  const bookingBasis = matches.data.basis === "booking";
  const whose = bookingBasis ? "this booking" : "these answers";
  const bookedWith = bookingBasis ? matches.data.bookedWith : undefined;
  const mismatch = () => {
    if (expectedVersion !== undefined)
      void run({ action: "mismatch", expectedVersion, acknowledgedMatchIds: ids });
  };

  if (correcting !== null && bookingDateOfBirth !== undefined && expectedVersion !== undefined) {
    return (
      <div className="intake-review-reconciler" ref={root}>
        <CorrectDateOfBirth
          match={correcting}
          bookingDateOfBirth={bookingDateOfBirth}
          busy={busy !== null}
          onCancel={() => {
            refocusCorrect.current = correcting.patientId;
            setCorrecting(null);
            setError(null);
          }}
          onConfirm={() =>
            void run({
              action: "link",
              expectedVersion,
              patientId: correcting.patientId,
              correctBookingDateOfBirth: true,
            })
          }
        />
        {error ? (
          <p className="schedule-modal-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="intake-review-reconciler" ref={root}>
      {bookedWith === undefined ? null : (
        <p className="intake-review-note">{BOOKED_WITH_NOTES[bookedWith]}</p>
      )}
      {open.length > 0 ? (
        <>
          <p className="intake-review-note" id={listId}>
            {bookingReview
              ? open.length === 1
                ? "A chart in this clinic matches this booking. Link it if it is the patient's chart, or mark it as not this patient."
                : "Charts in this clinic match this booking. Link the patient's chart, or mark them as not this patient."
              : "Link the patient's chart, or tick “None of these” below."}
          </p>
          {bookingBasis ? (
            <p className="intake-review-note">
              Check the patient's health card at the desk before linking.
            </p>
          ) : null}
          <ul className="intake-review-matches" aria-labelledby={listId}>
            {open.map((match) => (
              <MatchRow
                key={match.patientId}
                match={match}
                timeZone={timeZone}
                disabled={linkDisabled}
                linking={linkingId === match.patientId}
                onLink={link}
                onCorrect={onCorrect}
              />
            ))}
          </ul>
          {bookingReview ? (
            <div className="intake-review-actions">
              <button
                type="button"
                className="schedule-btn ghost"
                disabled={busy !== null || versionMissing}
                onClick={mismatch}
              >
                {busy === "mismatch"
                  ? "Saving…"
                  : open.length === 1
                    ? "Not this patient"
                    : "None of these is the patient"}
              </button>
            </div>
          ) : (
            <label className="schedule-modal-check">
              <input
                type="checkbox"
                checked={noneOfThese}
                disabled={busy !== null}
                onChange={(event) => setNoneOfThese(event.target.checked)}
              />
              None of these charts is this patient
            </label>
          )}
        </>
      ) : dismissed.length > 0 ? (
        <p className="intake-review-note">
          {bookingReview
            ? "Every chart matched for this booking was marked as not this patient. Link one below if that was a mistake, or find another chart."
            : "Every possible chart was ruled out. Link one below if that was a mistake."}
        </p>
      ) : (
        <p className="intake-review-note">No chart in this clinic matches {whose}.</p>
      )}
      {dismissed.length > 0 ? (
        <>
          <p className="intake-review-label" id={ruledOutId}>
            Ruled out for this booking
          </p>
          <ul className="intake-review-matches" aria-labelledby={ruledOutId}>
            {dismissed.map((match) => (
              <MatchRow
                key={match.patientId}
                match={match}
                timeZone={timeZone}
                disabled={linkDisabled}
                linking={linkingId === match.patientId}
                onLink={link}
                onCorrect={onCorrect}
              />
            ))}
          </ul>
        </>
      ) : null}
      {listPatientDirectory === undefined ? null : (
        <ChartSearch
          listPatientDirectory={listPatientDirectory}
          siteId={siteId}
          disabled={linkDisabled}
          linkingId={linkingId}
          onLink={link}
        />
      )}
      {askForDetails !== undefined ? (
        askForDetails(open.length)
      ) : (
        <>
          {identityAvailable ? null : (
            <p className="intake-review-note">
              These answers don't include the patient's details: link a chart, or register the
              patient on the Patients page.
            </p>
          )}
          <div className="intake-review-actions">
            <button
              type="button"
              className="schedule-btn primary"
              disabled={busy !== null || !ruledOut || !identityAvailable || versionMissing}
              onClick={() =>
                expectedVersion === undefined
                  ? undefined
                  : void run({ action: "create", expectedVersion, acknowledgedMatchIds: ids })
              }
            >
              {busy === "create" ? "Creating chart…" : "Create a chart from these answers"}
            </button>
            {open.length > 0 && noneOfThese ? (
              <button
                type="button"
                className="schedule-btn ghost"
                disabled={busy !== null || versionMissing}
                onClick={mismatch}
              >
                {busy === "mismatch" ? "Saving…" : "Rule them out without creating a chart"}
              </button>
            ) : null}
          </div>
        </>
      )}
      {error ? (
        <p className="schedule-modal-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A walk-in's own chart is fixed (the desk picked or made it before the code), so its review
 * links nothing. The same matcher still lists other charts the answers match, such as a health
 * card already on another chart, so staff can see a possible duplicate. There is no chart merge:
 * this list only informs. An audited read, fetched when the review opens.
 */
export function WalkInChartDuplicates({
  appointmentId,
  client,
  siteId,
  timeZone,
  onOpenChart,
}: {
  readonly appointmentId: string;
  readonly client: Pick<IntakeReviewClient, "getSchedulingPatientMatches">;
  readonly siteId: string;
  readonly timeZone: string;
  readonly onOpenChart?: ((patientId: string, displayName: string) => void) | undefined;
}) {
  const listId = useId();
  const matches = useQuery({
    queryKey: schedulingPatientMatchesKey(siteId, appointmentId),
    queryFn: ({ signal }) => client.getSchedulingPatientMatches(appointmentId, signal),
    ...AUDITED_READ,
  });
  if (matches.isPending) {
    return (
      <p className="intake-review-note" role="status">
        Checking for other charts these answers match…
      </p>
    );
  }
  if (matches.isError) {
    return (
      <p className="intake-review-note" role="status">
        {intakeErrorMessage(matches.error, "The check for other charts could not be completed.")}
      </p>
    );
  }
  const others = matches.data.matches;
  if (others.length === 0) return null;
  return (
    <div className="intake-review-reconciler">
      <p className="intake-review-caution" id={listId}>
        These answers also match {others.length === 1 ? "another chart" : "other charts"}. If one is
        this patient, tell the clinic administrator.
      </p>
      <ul className="intake-review-matches" aria-labelledby={listId}>
        {others.map((match) => (
          <li key={match.patientId}>
            <div>
              <strong>{match.displayName}</strong>
              <span>{matchFacts(match, timeZone)}</span>
              <small>
                Matched on {match.matchedOn.map((signal) => SIGNAL_LABELS[signal]).join(", ")}
              </small>
            </div>
            {onOpenChart ? (
              <button
                type="button"
                className="schedule-btn ghost sm"
                aria-label={`Open the chart of ${match.displayName}, MRN ${match.mrn}`}
                onClick={() => onOpenChart(match.patientId, match.displayName)}
              >
                Open chart
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
